import { useState, useEffect, Fragment, useCallback } from 'react';
import api from '../../hooks/useApi';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { TIME_ENTRY_AUDIT_TOUR_ID, timeEntryAuditTourSteps } from './timeEntryAuditTour';

// Time Entry Audit: why logged time is not costing against SOW milestones.
//
// Two tabs over the same rows. Gaps by SOW leads with the size of the problem
// per SOW and drills to the entries behind it, with a milestone dropdown on
// each entry for those who may edit other people's time. The Ledger lists
// every entry with its route (task, project, overhead), the screen that
// logged it where recorded, its milestone and its rate, with filters for each.

const iso = (d) => d.toISOString().slice(0, 10);
const todayISO = () => iso(new Date());
const addDaysISO = (n) => iso(new Date(Date.now() + n * 86400000));
function monthStartISO(offset = 0) {
  const d = new Date();
  return iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset, 1)));
}
function monthEndISO(offset = 0) {
  const d = new Date();
  return iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset + 1, 0)));
}

const PRESETS = [
  { key: 'month',  label: 'This month',   range: () => [monthStartISO(0), todayISO()] },
  { key: 'last',   label: 'Last month',   range: () => [monthStartISO(-1), monthEndISO(-1)] },
  { key: '30',     label: 'Last 30 days', range: () => [addDaysISO(-30), todayISO()] },
  { key: '90',     label: 'Last 90 days', range: () => [addDaysISO(-90), todayISO()] },
  { key: 'all',    label: 'All time',     range: () => ['2000-01-01', todayISO()] },
];

const TABS = [
  { key: 'gaps',   label: 'Gaps by SOW' },
  { key: 'ledger', label: 'Ledger' },
];

const ROUTE_LABELS = { task: 'Task', project: 'Project (SOW)', overhead: 'Overhead' };
const SOURCE_LABELS = { 'my-time': 'My Time', 'quick-log': 'Quick log', 'timesheet': 'Timesheet', 'hours-analysis': 'Hours Analysis', 'leave': 'Approved leave' };

const fmtH = (v) => {
  const n = parseFloat(v) || 0;
  if (n === 0) return '0h';
  const h = Math.floor(n);
  const m = Math.round((n - h) * 60);
  return m ? `${h}h ${m}m` : `${h}h`;
};
const fmtDate = (s) => {
  if (!s) return '';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
};

const Warn = ({ children }) => <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-50 text-amber-700">{children}</span>;
const Ok = ({ children }) => <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-50 text-emerald-700">{children}</span>;
const Muted = ({ children }) => <span className="text-[9px] text-gray-400">{children}</span>;

// Milestone dropdown for one entry. Loads the SOW's milestones once per SOW
// (cached by the parent) and saves on change; the outcome shows beside the
// control, because that is where the person is looking.
function MilestoneCell({ entry, milestones, canEdit, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  if (!entry.sow_id) return <Muted>n/a</Muted>;
  if (!canEdit) {
    return entry.milestone_code ? <Ok>{entry.milestone_code}</Ok> : <Warn>none</Warn>;
  }
  const save = async (value) => {
    setBusy(true); setMsg('');
    try {
      await api.put(`time-entries/${entry.id}`, { milestone_id: value || null });
      setMsg('Saved');
      onSaved?.(entry.id, value || null);
    } catch (e) {
      setMsg(e?.message || 'Could not save');
    }
    setBusy(false);
  };
  return (
    <span className="inline-flex items-center gap-1">
      <select className={`form-input text-[9px] py-0.5 ${entry.milestone_id ? '' : 'border-amber-300 bg-amber-50'}`} style={{ width: 'auto' }}
        value={entry.milestone_id || ''} disabled={busy || !milestones} onChange={e => save(e.target.value)}>
        <option value="">{milestones && !milestones.length ? 'No milestones on SOW' : 'None'}</option>
        {(milestones || []).map(m => <option key={m.id} value={m.id}>{m.milestone_code}</option>)}
      </select>
      {msg && <span className={`text-[8px] ${msg === 'Saved' ? 'text-emerald-600' : 'text-rose-600'}`}>{msg}</span>}
    </span>
  );
}

function EntryRows({ entries, milestonesBySow, canEdit, onSaved, showSow = true }) {
  return entries.map(e => (
    <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50">
      <td className="px-2 py-1 text-[9px] whitespace-nowrap">{fmtDate(e.date)}</td>
      <td className="px-2 py-1 text-[9px] whitespace-nowrap">{e.person_name}</td>
      <td className="px-2 py-1 text-[9px] whitespace-nowrap">{ROUTE_LABELS[e.route] || e.route}</td>
      <td className="px-2 py-1 text-[9px] whitespace-nowrap">{e.source ? SOURCE_LABELS[e.source] || e.source : <Muted>not recorded</Muted>}</td>
      {showSow && (
        <td className="px-2 py-1 text-[9px] max-w-[220px] truncate" title={e.sow_detail || ''}>
          {e.sow_reference ? <span className="font-semibold">{e.sow_reference}</span> : <Muted>—</Muted>}
          {e.task_name && <span className="text-gray-500"> · {e.task_name}</span>}
          {e.overhead_category_name && <span className="text-gray-500">{e.overhead_category_name}</span>}
        </td>
      )}
      {!showSow && <td className="px-2 py-1 text-[9px] max-w-[220px] truncate">{e.task_name || e.description || <Muted>—</Muted>}</td>}
      <td className="px-2 py-1 text-[9px] whitespace-nowrap">
        <MilestoneCell entry={e} milestones={milestonesBySow[e.sow_id]} canEdit={canEdit} onSaved={onSaved} />
      </td>
      <td className="px-2 py-1 text-[9px] text-right whitespace-nowrap">{fmtH(e.hours)}</td>
      <td className="px-2 py-1 text-[9px] text-right whitespace-nowrap">{e.day_rate ? <Ok>set</Ok> : <Warn>none</Warn>}</td>
    </tr>
  ));
}

const ENTRY_HEAD = (showSow) => (
  <tr className="bg-gray-50 text-left text-[8px] font-bold text-gray-400 uppercase tracking-wide">
    <th className="px-2 py-1.5">Date</th>
    <th className="px-2 py-1.5">Person</th>
    <th className="px-2 py-1.5">Route</th>
    <th className="px-2 py-1.5">Screen</th>
    <th className="px-2 py-1.5">{showSow ? 'SOW / task' : 'Task / note'}</th>
    <th className="px-2 py-1.5">Milestone</th>
    <th className="px-2 py-1.5 text-right">Hours</th>
    <th className="px-2 py-1.5 text-right">Rate</th>
  </tr>
);

export default function TimeEntryAuditPage() {
  const [from, setFrom] = useState(monthStartISO(0));
  const [to, setTo] = useState(todayISO());
  const [tab, setTab] = useState('gaps');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [milestonesBySow, setMilestonesBySow] = useState({});

  // Gaps drill-down
  const [expanded, setExpanded] = useState(null);
  const [sowEntries, setSowEntries] = useState({});
  const [sowBusy, setSowBusy] = useState(false);

  // Ledger
  const [filters, setFilters] = useState({ milestone: '', rate: '', route: '', source: '', person: '', sow_id: '' });
  const [ledger, setLedger] = useState(null);
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 200;

  const tour = useFeatureTour(TIME_ENTRY_AUDIT_TOUR_ID, { enabled: !loading && !loadError });

  // `loading` blanks the tables; a refresh after a save (or a new range with
  // data already showing) must not, or the person's "Saved" note vanishes
  // with the row they were looking at. So it is only set when there is nothing
  // on screen yet.
  const loadSummary = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setLoadError('');
    try {
      const d = await api.get('time-entry-audit/summary', { from, to });
      setSummary(d);
    } catch (e) {
      setSummary(null); setLoadError(e?.message || 'Could not load');
    }
    setLoading(false);
  }, [from, to]);

  useEffect(() => {
    setExpanded(null); setSowEntries({}); setOffset(0);
    loadSummary();
  }, [loadSummary]);

  const ensureMilestones = useCallback(async (sowId) => {
    if (!sowId || milestonesBySow[sowId]) return;
    try {
      const ms = await api.get('milestones', { sow_id: sowId });
      const live = (ms || []).filter(m => !['Cancelled', 'Split', 'Credit Noted'].includes(m.status));
      setMilestonesBySow(prev => ({ ...prev, [sowId]: live }));
    } catch { setMilestonesBySow(prev => ({ ...prev, [sowId]: [] })); }
  }, [milestonesBySow]);

  const toggleSow = async (sowId) => {
    if (expanded === sowId) { setExpanded(null); return; }
    setExpanded(sowId);
    ensureMilestones(sowId);
    if (!sowEntries[sowId]) {
      setSowBusy(true);
      try {
        const d = await api.get('time-entry-audit/entries', { from, to, sow_id: sowId, limit: 500 });
        setSowEntries(prev => ({ ...prev, [sowId]: d.entries || [] }));
      } catch { setSowEntries(prev => ({ ...prev, [sowId]: [] })); }
      setSowBusy(false);
    }
  };

  const loadLedger = useCallback(async () => {
    setLedgerBusy(true);
    try {
      const q = { from, to, limit: LIMIT, offset };
      for (const [k, v] of Object.entries(filters)) if (v) q[k] = v;
      const d = await api.get('time-entry-audit/entries', q);
      setLedger(d);
      const sowIds = [...new Set((d.entries || []).map(e => e.sow_id).filter(Boolean))];
      for (const id of sowIds) ensureMilestones(id);
    } catch { setLedger({ total: 0, entries: [] }); }
    setLedgerBusy(false);
  }, [from, to, filters, offset, ensureMilestones]);

  useEffect(() => { if (tab === 'ledger') loadLedger(); }, [tab, loadLedger]);

  // After a milestone is assigned in either view, patch the row locally and
  // refresh the totals so the tiles and the SOW row move with it.
  const onSaved = (entryId, milestoneId) => {
    const code = (id, sowId) => (milestonesBySow[sowId] || []).find(m => m.id === id)?.milestone_code || null;
    const patch = (rows) => rows.map(e => e.id === entryId ? { ...e, milestone_id: milestoneId, milestone_code: code(milestoneId, e.sow_id) } : e);
    setSowEntries(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, patch(v)])));
    setLedger(prev => prev ? { ...prev, entries: patch(prev.entries) } : prev);
    loadSummary({ quiet: true });
  };

  const applyPreset = (p) => { const [f, t] = p.range(); setFrom(f); setTo(t); };
  const setFilter = (k, v) => { setOffset(0); setFilters(f => ({ ...f, [k]: v })); };

  const t = summary?.totals || {};
  const canEdit = !!summary?.can_edit;
  const sowTotalHours = (summary?.sows || []).reduce((s, r) => s + (r.hours || 0), 0);

  return (
    <div className="p-4">
      <div data-tour="page-header" className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h1 className="text-[15px] font-black text-navy flex items-center gap-2">
            Time Entry Audit
            <button onClick={tour.start} title="Replay the guided tour" aria-label="Replay the guided tour"
              className="w-4 h-4 rounded-full border border-gray-300 text-[9px] text-gray-500 hover:bg-gray-100 leading-none">?</button>
          </h1>
          <div className="text-[9px] text-gray-500 mt-0.5">Why logged time is not costing against SOW milestones: entries with no milestone or no day rate, and the route they came through.</div>
        </div>
      </div>

      <div data-tour="summary">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="flex gap-1 flex-wrap">
            {PRESETS.map(p => (
              <button key={p.key} onClick={() => applyPreset(p)}
                className="px-2 py-1 text-[9px] border border-gray-200 rounded bg-white hover:bg-gray-50 font-semibold text-gray-600">{p.label}</button>
            ))}
          </div>
          <span className="text-gray-300">·</span>
          <label className="text-[9px] text-gray-500 flex items-center gap-1">From
            <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} className="form-input text-[9px] py-0.5" /></label>
          <label className="text-[9px] text-gray-500 flex items-center gap-1">To
            <input type="date" value={to} min={from} max={todayISO()} onChange={e => setTo(e.target.value)} className="form-input text-[9px] py-0.5" /></label>
          {loading && <span className="text-[9px] text-gray-400">Loading…</span>}
          {loadError && <span className="text-[9px] text-rose-600">{loadError}</span>}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-4">
          {[
            { label: 'Hours logged', value: fmtH(t.hours), color: 'text-navy' },
            { label: 'No milestone', value: fmtH(t.no_milestone_hours), color: t.no_milestone_hours ? 'text-amber-600' : 'text-emerald-600', sub: t.hours ? `${Math.round((t.no_milestone_hours / t.hours) * 100)}% of hours` : '' },
            { label: 'No day rate', value: fmtH(t.no_rate_hours), color: t.no_rate_hours ? 'text-rose-600' : 'text-emerald-600' },
            { label: 'Via task', value: fmtH(t.task_hours), color: 'text-blue-600', sub: t.hours ? `${Math.round((t.task_hours / t.hours) * 100)}%` : '' },
            { label: 'Via project (SOW)', value: fmtH(t.project_hours), color: 'text-teal-600', sub: t.hours ? `${Math.round((t.project_hours / t.hours) * 100)}%` : '' },
            { label: 'Overhead', value: fmtH(t.overhead_hours), color: 'text-gray-600' },
          ].map(s => (
            <div key={s.label} className="bg-white border border-gray-100 rounded-lg px-3 py-2 text-center">
              <div className={`text-[16px] font-black ${s.color}`}>{s.value}</div>
              <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wide mt-0.5">{s.label}</div>
              {s.sub && <div className="text-[8px] text-gray-400">{s.sub}</div>}
            </div>
          ))}
        </div>
      </div>

      <div data-tour="tabs" className="flex rounded-lg border border-gray-200 overflow-hidden w-fit mb-3">
        {TABS.map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)}
            className={`px-3 py-1.5 text-[10px] font-bold border-r last:border-r-0 transition-colors ${tab === tb.key ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'gaps' && (
        <div data-tour="gaps-table">
          <div className="text-[8px] text-gray-400 mb-1">Worst first. Click a SOW to see the entries behind it{canEdit ? ' and assign milestones in place' : ''}.</div>
          <div className="bg-white border border-gray-200 rounded overflow-x-auto">
            {loading ? (
              <div className="text-center py-12 text-[10px] text-gray-400">Loading…</div>
            ) : !summary?.sows?.length ? (
              <div className="text-center py-12 text-[10px] text-gray-400">No SOW time logged in this period.</div>
            ) : (
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="bg-gray-50 text-left text-[8px] font-bold text-gray-400 uppercase tracking-wide">
                    <th className="px-2 py-1.5">SOW</th>
                    <th className="px-2 py-1.5 text-right">Hours</th>
                    <th className="px-2 py-1.5 text-right">No milestone</th>
                    <th className="px-2 py-1.5 text-right">No rate</th>
                    <th className="px-2 py-1.5">Mostly logged via</th>
                    <th className="px-2 py-1.5 text-right">Via task</th>
                    <th className="px-2 py-1.5 text-right">Via project</th>
                    <th className="px-2 py-1.5 text-right">Milestones on SOW</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.sows.map(r => (
                    <Fragment key={r.sow_id}>
                      <tr onClick={() => toggleSow(r.sow_id)} className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50 ${expanded === r.sow_id ? 'bg-gray-50' : ''}`}>
                        <td className="px-2 py-1.5 text-[9px]">
                          <span className="text-gray-400 mr-1">{expanded === r.sow_id ? '▾' : '▸'}</span>
                          <span className="font-semibold">{r.sow_reference || 'Unknown SOW'}</span>
                          {r.sow_detail && <span className="text-gray-500"> · {r.sow_detail}</span>}
                        </td>
                        <td className="px-2 py-1.5 text-[9px] text-right whitespace-nowrap">{fmtH(r.hours)}</td>
                        <td className="px-2 py-1.5 text-[9px] text-right whitespace-nowrap">
                          {r.no_milestone_hours ? <Warn>{fmtH(r.no_milestone_hours)} ({r.no_milestone_pct}%)</Warn> : <Ok>0h</Ok>}
                        </td>
                        <td className="px-2 py-1.5 text-[9px] text-right whitespace-nowrap">
                          {r.no_rate_hours ? <Warn>{fmtH(r.no_rate_hours)}</Warn> : <Ok>0h</Ok>}
                        </td>
                        <td className="px-2 py-1.5 text-[9px]">{r.top_route === 'task' ? 'Task entries' : 'Project entries'}</td>
                        <td className="px-2 py-1.5 text-[9px] text-right whitespace-nowrap">{fmtH(r.task_hours)}</td>
                        <td className="px-2 py-1.5 text-[9px] text-right whitespace-nowrap">{fmtH(r.project_hours)}</td>
                        <td className="px-2 py-1.5 text-[9px] text-right">{r.milestone_count ? r.milestone_count : <Warn>0</Warn>}</td>
                      </tr>
                      {expanded === r.sow_id && (
                        <tr className="border-t border-gray-100">
                          <td colSpan={8} className="p-2 bg-gray-50">
                            {sowBusy && !sowEntries[r.sow_id] ? (
                              <div className="text-[9px] text-gray-400 py-2">Loading entries…</div>
                            ) : !sowEntries[r.sow_id]?.length ? (
                              <div className="text-[9px] text-gray-400 py-2">No entries.</div>
                            ) : (
                              <div className="bg-white border border-gray-200 rounded overflow-x-auto">
                                <table className="w-full min-w-[640px]">
                                  <thead>{ENTRY_HEAD(false)}</thead>
                                  <tbody>
                                    <EntryRows entries={sowEntries[r.sow_id]} milestonesBySow={milestonesBySow} canEdit={canEdit} onSaved={onSaved} showSow={false} />
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-200 bg-gray-50 font-bold">
                    <td className="px-2 py-1.5 text-[9px]">{summary.sows.length} SOWs</td>
                    <td className="px-2 py-1.5 text-[9px] text-right">{fmtH(sowTotalHours)}</td>
                    <td className="px-2 py-1.5 text-[9px] text-right">{fmtH(t.no_milestone_hours)}</td>
                    <td colSpan={5} />
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'ledger' && (
        <div>
          <div data-tour="ledger-filters" className="flex items-center gap-2 mb-2 flex-wrap">
            <select className="form-input text-[9px] py-0.5" style={{ width: 'auto' }} value={filters.milestone} onChange={e => setFilter('milestone', e.target.value)}>
              <option value="">Milestone: any</option>
              <option value="missing">Milestone: missing</option>
              <option value="set">Milestone: set</option>
            </select>
            <select className="form-input text-[9px] py-0.5" style={{ width: 'auto' }} value={filters.rate} onChange={e => setFilter('rate', e.target.value)}>
              <option value="">Rate: any</option>
              <option value="missing">Rate: missing</option>
              <option value="set">Rate: set</option>
            </select>
            <select className="form-input text-[9px] py-0.5" style={{ width: 'auto' }} value={filters.route} onChange={e => setFilter('route', e.target.value)}>
              <option value="">Route: any</option>
              {Object.entries(ROUTE_LABELS).map(([k, v]) => <option key={k} value={k}>Route: {v}</option>)}
            </select>
            <select className="form-input text-[9px] py-0.5" style={{ width: 'auto' }} value={filters.source} onChange={e => setFilter('source', e.target.value)}>
              <option value="">Screen: any</option>
              {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>Screen: {v}</option>)}
              <option value="none">Screen: not recorded</option>
            </select>
            <select className="form-input text-[9px] py-0.5" style={{ width: 'auto' }} value={filters.sow_id} onChange={e => setFilter('sow_id', e.target.value)}>
              <option value="">SOW: any</option>
              {(summary?.sows || []).map(s => <option key={s.sow_id} value={s.sow_id}>{s.sow_reference || 'Unknown SOW'}</option>)}
            </select>
            <input className="form-input text-[9px] py-0.5" style={{ width: '8rem' }} placeholder="Person" value={filters.person}
              onChange={e => setFilter('person', e.target.value)} />
            {ledgerBusy && <span className="text-[9px] text-gray-400">Loading…</span>}
            {ledger && !ledgerBusy && (
              <span className="text-[9px] text-gray-500 ml-auto">
                {ledger.total ? `${offset + 1}–${Math.min(offset + LIMIT, ledger.total)} of ${ledger.total}` : '0 entries'}
                <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}
                  className="ml-2 px-1.5 py-0.5 border border-gray-200 rounded bg-white disabled:opacity-40">‹</button>
                <button disabled={offset + LIMIT >= (ledger.total || 0)} onClick={() => setOffset(o => o + LIMIT)}
                  className="ml-1 px-1.5 py-0.5 border border-gray-200 rounded bg-white disabled:opacity-40">›</button>
              </span>
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded overflow-x-auto">
            {!ledger || (ledgerBusy && !ledger.entries?.length) ? (
              <div className="text-center py-12 text-[10px] text-gray-400">Loading…</div>
            ) : !ledger.entries?.length ? (
              <div className="text-center py-12 text-[10px] text-gray-400">No entries match.</div>
            ) : (
              <table className="w-full min-w-[820px]">
                <thead>{ENTRY_HEAD(true)}</thead>
                <tbody>
                  <EntryRows entries={ledger.entries} milestonesBySow={milestonesBySow} canEdit={canEdit} onSaved={onSaved} />
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tour.open && <FeatureTour steps={timeEntryAuditTourSteps} onFinish={tour.finish} onSkip={tour.skip} />}
    </div>
  );
}
